import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Divider,
  Alert,
  MenuItem,
} from "@mui/material";
import { ContextSession } from "../../../services/ContextSession";

/* ================= CONFIG ================= */

const API_ENCUESTAS = "https://bei360.injupemp.gob.hn:8444/api/Encuesta/";
const API_AGENTES = "https://chatbot.injupemp.gob.hn/obtener_agentes/";
const API_RNP = "https://bei360.injupemp.gob.hn:8444/api/rnp/nombreRNP/";

/* ================= AUTOEVALUACIÓN ================= */

const retroConfig = [
  {
    key: "saludo",
    label: "El saludo y la cortesía del agente fueron adecuados",
    max: 20,
  },
  { key: "escucha", label: "Escucho y entendió su solicitud", max: 30 },
  { key: "solucion", label: "La solución brindad fue efectiva", max: 25 },
  { key: "general", label: "Como califica la atención en general", max: 25 },
];

/* ================= COMPONENTE ================= */

const EncuestasPage = () => {
  const { sessionData } = useContext(ContextSession);

  const TOKEN = sessionData?.token;
  const USUARIO_REGISTRO = sessionData?.username || "Desconocido";
  const USUARIO_ID = sessionData?.id;

  const [agenteUsuario, setAgenteUsuario] = useState(null);
  const [agentes, setAgentes] = useState([]);
  const [encuestas, setEncuestas] = useState([]);

  const [dni, setDni] = useState("");
  const [participante, setParticipante] = useState("");
  const [oficialSeleccionado, setOficialSeleccionado] = useState("");
  const [telefono, setTelefono] = useState("");

  const [retro, setRetro] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [error, setError] = useState("");

  /* ================= DATA ================= */

  const cargarEncuestas = async () => {
    const res = await fetch(API_ENCUESTAS, {
      headers: { Authorization: `Bearer ${TOKEN}` },
    });
    const data = await res.json();
    const lista = Array.isArray(data) ? data : data.results || [];
    setEncuestas(lista.filter((e) => e.UsuarioRegistro === USUARIO_REGISTRO));
  };

  const cargarAgentes = async () => {
    const res = await fetch(API_AGENTES);
    const data = await res.json();

    const disponibles = (data.agentes || []).filter((a) => a.disponible);
    setAgentes(disponibles);

    const agente = disponibles.find((a) => String(a.id) === String(USUARIO_ID));
    if (agente) setAgenteUsuario(agente);
  };

  useEffect(() => {
    if (TOKEN) {
      cargarEncuestas();
      cargarAgentes();
    }
  }, [TOKEN]);

  /* ================= NUEVA ENCUESTA ================= */

  const abrirNuevaEncuesta = () => {
    setDni("");
    setParticipante("");
    setTelefono("");
    setRetro({});
    setError("");

    // 🔥 PRESELECCIÓN AUTOMÁTICA DEL AGENTE
    setOficialSeleccionado(agenteUsuario?.nombre || "");

    setOpenModal(true);
  };

  /* ================= DNI ================= */

  const buscarNombrePorDni = async (dniValue) => {
    try {
      const res = await fetch(API_RNP, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dni: dniValue }),
      });
      const data = await res.json();
      setParticipante(data?.nombre || "");
    } catch {
      setParticipante("");
    }
  };

  /* ================= GUARDAR ================= */

  const guardarEncuesta = async () => {
    if (!/^\d{13}$/.test(dni)) return setError("El DNI debe tener 13 dígitos");
    if (!participante) return setError("No se encontró el participante");
    if (!oficialSeleccionado) return setError("Seleccione el oficial");
    if (!telefono) return setError("El teléfono es obligatorio");

    const payload = {
      NombreParticipante: participante,
      DNI: dni,
      OficialNombre: oficialSeleccionado,
      Telefono: telefono,

      Criterio_Saludo: 0,
      Criterio_Identificacion: 0,
      Criterio_Escucha: 0,
      Criterio_Resolucion: 0,
      Criterio_Objeciones: 0,
      Criterio_Cierre: 0,
      Criterio_Protocolo: 0,

      Retro_Saludo: retro.saludo || 0,
      Retro_Escucha: retro.escucha || 0,
      Retro_Solucion: retro.solucion || 0,
      Retro_General: retro.general || 0,

      TotalCriterios: 0,
      TotalRetroalimentacion: Object.values(retro).reduce((a, b) => a + b, 0),

      UsuarioRegistro: USUARIO_REGISTRO,
    };

    await fetch(API_ENCUESTAS, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${TOKEN}`,
      },
      body: JSON.stringify(payload),
    });

    setOpenModal(false);
    cargarEncuestas();
  };

  /* ================= UI ================= */

  return (
    <Box maxWidth={1300} mx="auto" mt={4}>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography variant="h5">Encuestas</Typography>
          <Button variant="contained" onClick={abrirNuevaEncuesta}>
            Nueva Encuesta
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Participante</TableCell>
                <TableCell>DNI</TableCell>
                <TableCell>Oficial</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {encuestas.map((e, i) => (
                <TableRow key={i}>
                  <TableCell>
                    {e.FechaRegistro?.replace("T", " ").substring(0, 19)}
                  </TableCell>
                  <TableCell>{e.NombreParticipante}</TableCell>
                  <TableCell>{e.DNI}</TableCell>
                  <TableCell>{e.OficialNombre}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ================= MODAL ================= */}
      <Dialog open={openModal} maxWidth="md" fullWidth>
        <DialogTitle>Nueva Encuesta</DialogTitle>

        <DialogContent>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2} mb={2}>
            <Grid item xs={6}>
              <TextField
                label="DNI"
                fullWidth
                value={dni}
                inputProps={{ maxLength: 13 }}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^\d*$/.test(v)) {
                    setDni(v);
                    if (v.length === 13) buscarNombrePorDni(v);
                  }
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Nombre del participante"
                fullWidth
                value={participante}
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Oficial que atendió"
                value={oficialSeleccionado}
                onChange={(e) => setOficialSeleccionado(e.target.value)}
                InputProps={{ readOnly: true }}
              >
                {agentes.map((a) => (
                  <MenuItem key={a.id} value={a.nombre}>
                    {a.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Teléfono del participante"
                fullWidth
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
              />
            </Grid>
          </Grid>

          <Typography variant="h6">Auto Evaluación</Typography>
          <Grid container spacing={2}>
            {retroConfig.map((r) => (
              <Grid item xs={6} key={r.key}>
                <TextField
                  select
                  fullWidth
                  label={r.label}
                  value={retro[r.key] === r.max ? "SI" : "NO"}
                  onChange={(e) =>
                    setRetro((prev) => ({
                      ...prev,
                      [r.key]: e.target.value === "SI" ? r.max : 0,
                    }))
                  }
                >
                  <MenuItem value="SI">SI</MenuItem>
                  <MenuItem value="NO">NO</MenuItem>
                </TextField>
              </Grid>
            ))}
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button color="error" onClick={() => setOpenModal(false)}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={guardarEncuesta}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EncuestasPage;
